// PhotographyContact.js - Photography-themed contact section
import { v4 as uuidv4 } from 'uuid';
import { DEVICES } from '../../../constants/breakpoints';

/**
 * Creates a photography-themed contact section with form and contact info
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The contact section elements
 */
export const createPhotographyContactElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Class names
  const sectionClass = 'photography-contact-section';
  const containerClass = 'photography-contact-container';
  const headerClass = 'photography-contact-header';
  const titleClass = 'photography-contact-title';
  const subtitleClass = 'photography-contact-subtitle';
  const contentClass = 'photography-contact-content';
  const leftColumnClass = 'photography-contact-left';
  const rightColumnClass = 'photography-contact-right';
  
  // Contact info classes
  const infoContainerClass = 'photography-contact-info-container';
  const infoItemClass = 'photography-contact-info-item';
  const infoIconClass = 'photography-contact-info-icon';
  const infoContentClass = 'photography-contact-info-content';
  const infoTitleClass = 'photography-contact-info-title';
  const infoTextClass = 'photography-contact-info-text';
  
  // Form classes
  const formContainerClass = 'photography-contact-form-container';
  const formTitleClass = 'photography-contact-form-title';
  const formClass = 'photography-contact-form';
  const formRowClass = 'photography-contact-form-row';
  const formGroupClass = 'photography-contact-form-group';
  const formLabelClass = 'photography-contact-form-label';
  const formInputClass = 'photography-contact-form-input';
  const formTextareaClass = 'photography-contact-form-textarea';
  const formButtonClass = 'photography-contact-form-button';
  
  // Social classes
  const socialContainerClass = 'photography-contact-social-container';
  const socialTitleClass = 'photography-contact-social-title';
  const socialLinksClass = 'photography-contact-social-links';
  const socialLinkClass = 'photography-contact-social-link';

  // Desktop Styles
  const sectionStyles = {
    position: 'relative',
    paddingTop: '120px',
    paddingBottom: '120px',
    paddingLeft: '40px',
    paddingRight: '40px',
    backgroundColor: '#0a0a0a',
    backgroundImage: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 50%, #0a0a0a 100%)',
    color: '#ffffff',
    overflow: 'hidden'
  };

  const containerStyles = {
    maxWidth: '1400px',
    marginLeft: 'auto',
    marginRight: 'auto',
    position: 'relative',
    zIndex: '2'
  };

  const headerStyles = {
    textAlign: 'center',
    marginBottom: '80px'
  };

  const titleStyles = {
    fontSize: '48px',
    fontWeight: '300',
    color: '#ffffff',
    marginBottom: '24px',
    letterSpacing: '2px',
    fontFamily: '"Playfair Display", serif'
  };

  const subtitleStyles = {
    fontSize: '18px',
    color: '#b0b0b0',
    lineHeight: '1.6',
    maxWidth: '600px',
    marginLeft: 'auto',
    marginRight: 'auto',
    fontWeight: '300'
  };

  const contentStyles = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '80px',
    alignItems: 'start'
  };

  const leftColumnStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '50px'
  };

  const rightColumnStyles = {
    display: 'flex',
    flexDirection: 'column'
  };

  const infoContainerStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '40px'
  };

  const infoItemStyles = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '20px',
    padding: '30px',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: '12px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(10px)',
    transition: 'all 0.3s ease'
  };

  const infoItemStylesMobile = {
    display: 'block',
  };

  const infoIconStyles = {
    width: '50px',
    height: '50px',
    backgroundColor: '#d4af37',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    color: '#000000',
    flexShrink: '0'
  };

  const infoContentStyles = {
    flex: '1'
  };

  const infoTitleStyles = {
    fontSize: '18px',
    fontWeight: '500',
    color: '#ffffff',
    marginBottom: '8px'
  };

  const infoTextStyles = {
    fontSize: '16px',
    color: '#b0b0b0',
    lineHeight: '1.5'
  };

  const formContainerStyles = {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: '16px',
    padding: '50px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(10px)'
  };

  const formTitleStyles = {
    fontSize: '32px',
    fontWeight: '300',
    color: '#ffffff',
    marginBottom: '40px',
    textAlign: 'center',
    fontFamily: '"Playfair Display", serif'
  };

  const formStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '30px'
  };

  const formRowStyles = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px'
  };

  const formGroupStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  };

  const formLabelStyles = {
    fontSize: '14px',
    fontWeight: '500',
    color: '#d4af37',
    textTransform: 'uppercase',
    letterSpacing: '1px'
  };

  const formInputStyles = {
    padding: '16px 20px',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '8px',
    color: '#ffffff',
    fontSize: '16px',
    transition: 'all 0.3s ease',
    outline: 'none'
  };

  const formTextareaStyles = {
    padding: '16px 20px',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '8px',
    color: '#ffffff',
    fontSize: '16px',
    minHeight: '120px',
    resize: 'vertical',
    transition: 'all 0.3s ease',
    outline: 'none'
  };

  const formButtonStyles = {
    padding: '18px 40px',
    backgroundColor: '#d4af37',
    color: '#000000',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    alignSelf: 'flex-start'
  };

  const socialContainerStyles = {
    textAlign: 'center'
  };

  const socialTitleStyles = {
    fontSize: '20px',
    fontWeight: '500',
    color: '#ffffff',
    marginBottom: '30px'
  };

  const socialLinksStyles = {
    display: 'flex',
    justifyContent: 'center',
    gap: '20px'
  };

  const socialLinkStyles = {
    width: '50px',
    height: '50px',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#d4af37',
    fontSize: '20px',
    transition: 'all 0.3s ease',
    border: '1px solid rgba(255, 255, 255, 0.2)'
  };

  // Tablet Styles
  const tabletContentStyles = {
    gridTemplateColumns: '1fr',
    gap: '60px'
  };

  const tabletFormRowStyles = {
    gridTemplateColumns: '1fr'
  };

  const tabletTitleStyles = {
    fontSize: '40px'
  };

  // Mobile Styles
  const mobileSectionStyles = {
    paddingTop: '80px',
    paddingBottom: '80px',
    paddingLeft: '20px',
    paddingRight: '20px'
  };

  const mobileTitleStyles = {
    fontSize: '32px'
  };

  const mobileFormContainerStyles = {
    padding: '30px 20px'
  };

  const mobileFormTitleStyles = {
    fontSize: '24px'
  };

  // Register styles
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(sectionClass, sectionStyles);
    newRegistry[DEVICES.DESKTOP].set(containerClass, containerStyles);
    newRegistry[DEVICES.DESKTOP].set(headerClass, headerStyles);
    newRegistry[DEVICES.DESKTOP].set(titleClass, titleStyles);
    newRegistry[DEVICES.DESKTOP].set(subtitleClass, subtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(contentClass, contentStyles);
    newRegistry[DEVICES.DESKTOP].set(leftColumnClass, leftColumnStyles);
    newRegistry[DEVICES.DESKTOP].set(rightColumnClass, rightColumnStyles);
    newRegistry[DEVICES.DESKTOP].set(infoContainerClass, infoContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(infoItemClass, infoItemStyles);
    newRegistry[DEVICES.DESKTOP].set(infoIconClass, infoIconStyles);
    newRegistry[DEVICES.DESKTOP].set(infoContentClass, infoContentStyles);
    newRegistry[DEVICES.DESKTOP].set(infoTitleClass, infoTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(infoTextClass, infoTextStyles);
    newRegistry[DEVICES.DESKTOP].set(formContainerClass, formContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(formTitleClass, formTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(formClass, formStyles);
    newRegistry[DEVICES.DESKTOP].set(formRowClass, formRowStyles);
    newRegistry[DEVICES.DESKTOP].set(formGroupClass, formGroupStyles);
    newRegistry[DEVICES.DESKTOP].set(formLabelClass, formLabelStyles);
    newRegistry[DEVICES.DESKTOP].set(formInputClass, formInputStyles);
    newRegistry[DEVICES.DESKTOP].set(formTextareaClass, formTextareaStyles);
    newRegistry[DEVICES.DESKTOP].set(formButtonClass, formButtonStyles);
    newRegistry[DEVICES.DESKTOP].set(socialContainerClass, socialContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(socialTitleClass, socialTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(socialLinksClass, socialLinksStyles);
    newRegistry[DEVICES.DESKTOP].set(socialLinkClass, socialLinkStyles);
    
    // Tablet styles
    newRegistry[DEVICES.TABLET].set(contentClass, tabletContentStyles);
    newRegistry[DEVICES.TABLET].set(formRowClass, tabletFormRowStyles);
    newRegistry[DEVICES.TABLET].set(titleClass, tabletTitleStyles);
    
    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(sectionClass, mobileSectionStyles);
    newRegistry[DEVICES.MOBILE].set(titleClass, mobileTitleStyles);
    newRegistry[DEVICES.MOBILE].set(formContainerClass, mobileFormContainerStyles);
    newRegistry[DEVICES.MOBILE].set(formTitleClass, mobileFormTitleStyles);
    newRegistry[DEVICES.MOBILE].set(infoItemClass, infoItemStylesMobile);

    return newRegistry;
  });

  // Add classes to global registry
  setGlobalClassNames(prev => new Set([
    ...prev,
    sectionClass, containerClass, headerClass, titleClass, subtitleClass,
    contentClass, leftColumnClass, rightColumnClass, infoContainerClass,
    infoItemClass, infoIconClass, infoContentClass, infoTitleClass, infoTextClass,
    formContainerClass, formTitleClass, formClass, formRowClass, formGroupClass,
    formLabelClass, formInputClass, formTextareaClass, formButtonClass,
    socialContainerClass, socialTitleClass, socialLinksClass, socialLinkClass
  ]));

  // Generate unique IDs
  const sectionId = Date.now();
  const containerId = sectionId + 1;
  const headerId = sectionId + 2;
  const titleId = sectionId + 3;
  const subtitleId = sectionId + 4;
  const contentId = sectionId + 5;
  const leftColumnId = sectionId + 6;
  const rightColumnId = sectionId + 7;
  
  // Contact info IDs
  const infoContainerId = sectionId + 8;
  const addressItemId = sectionId + 9;
  const addressIconId = sectionId + 10;
  const addressContentId = sectionId + 11;
  const addressTitleId = sectionId + 12;
  const addressTextId = sectionId + 13;
  
  const phoneItemId = sectionId + 14;
  const phoneIconId = sectionId + 15;
  const phoneContentId = sectionId + 16;
  const phoneTitleId = sectionId + 17;
  const phoneTextId = sectionId + 18;
  
  const emailItemId = sectionId + 19;
  const emailIconId = sectionId + 20;
  const emailContentId = sectionId + 21;
  const emailTitleId = sectionId + 22;
  const emailTextId = sectionId + 23;
  
  // Social IDs
  const socialContainerId = sectionId + 24;
  const socialTitleId = sectionId + 25;
  const socialLinksId = sectionId + 26;
  const instagramLinkId = sectionId + 27;
  const facebookLinkId = sectionId + 28;
  const twitterLinkId = sectionId + 29;
  
  // Form IDs
  const formContainerId = sectionId + 30;
  const formTitleId = sectionId + 31;
  const formId = sectionId + 32;
  
  const nameEmailRowId = sectionId + 33;
  const nameGroupId = sectionId + 34;
  const nameLabelId = sectionId + 35;
  const nameInputId = sectionId + 36;
  const emailGroupId = sectionId + 37;
  const emailLabelId = sectionId + 38;
  const emailInputId = sectionId + 39;
  
  const subjectGroupId = sectionId + 40;
  const subjectLabelId = sectionId + 41;
  const subjectInputId = sectionId + 42;
  
  const messageGroupId = sectionId + 43;
  const messageLabelId = sectionId + 44;
  const messageTextareaId = sectionId + 45;
  
  const submitButtonId = sectionId + 46;

  const elements = [
    // Main section
    {
      id: sectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Photography Contact Section',
      parentId: null,
      className: sectionClass,
      styles: {},
      children: [containerId],
      text: '',
      attributes: {
        'data-element': 'photography-contact-section',
        'data-component': 'section'
      }
    },

    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Contact Container',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: containerClass,
      styles: {},
      children: [headerId, contentId],
      text: '',
      attributes: {
        'data-element': 'contact-container'
      }
    },

    // Header
    {
      id: headerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Contact Header',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: headerClass,
      styles: {},
      children: [titleId, subtitleId],
      text: '',
      attributes: {
        'data-element': 'contact-header'
      }
    },

    // Title
    {
      id: titleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h2',
      name: 'Contact Title',
      parentId: headerId,
      hierarchyName: getHierarchyName('h2'),
      className: titleClass,
      styles: {},
      children: [],
      text: 'Let\'s Create Something Beautiful Together',
      attributes: {
        'data-element': 'contact-title'
      }
    },

    // Subtitle
    {
      id: subtitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Contact Subtitle',
      parentId: headerId,
      hierarchyName: getHierarchyName('p'),
      className: subtitleClass,
      styles: {},
      children: [],
      text: 'Ready to capture your special moments? Get in touch to discuss your photography needs and let\'s bring your vision to life.',
      attributes: {
        'data-element': 'contact-subtitle'
      }
    },

    // Content
    {
      id: contentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Contact Content',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: contentClass,
      styles: {},
      children: [leftColumnId, rightColumnId],
      text: '',
      attributes: {
        'data-element': 'contact-content'
      }
    },

    // Left Column
    {
      id: leftColumnId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Contact Left Column',
      parentId: contentId,
      hierarchyName: getHierarchyName('div'),
      className: leftColumnClass,
      styles: {},
      children: [infoContainerId, socialContainerId],
      text: '',
      attributes: {
        'data-element': 'contact-left-column'
      }
    },

    // Right Column
    {
      id: rightColumnId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Contact Right Column',
      parentId: contentId,
      hierarchyName: getHierarchyName('div'),
      className: rightColumnClass,
      styles: {},
      children: [formContainerId],
      text: '',
      attributes: {
        'data-element': 'contact-right-column'
      }
    },

    // Contact Info Container
    {
      id: infoContainerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Contact Info Container',
      parentId: leftColumnId,
      hierarchyName: getHierarchyName('div'),
      className: infoContainerClass,
      styles: {},
      children: [addressItemId, phoneItemId, emailItemId],
      text: '',
      attributes: {
        'data-element': 'contact-info-container'
      }
    },

    // Address Item
    {
      id: addressItemId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Address Item',
      parentId: infoContainerId,
      hierarchyName: getHierarchyName('div'),
      className: infoItemClass,
      styles: {},
      children: [addressIconId, addressContentId],
      text: '',
      attributes: {
        'data-element': 'contact-info-item'
      }
    },

    // Address Icon
    {
      id: addressIconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Address Icon',
      parentId: addressItemId,
      hierarchyName: getHierarchyName('div'),
      className: infoIconClass,
      styles: {},
      children: [],
      text: '📍',
      attributes: {
        'data-element': 'contact-info-icon'
      }
    },

    // Address Content
    {
      id: addressContentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Address Content',
      parentId: addressItemId,
      hierarchyName: getHierarchyName('div'),
      className: infoContentClass,
      styles: {},
      children: [addressTitleId, addressTextId],
      text: '',
      attributes: {
        'data-element': 'contact-info-content'
      }
    },

    // Address Title
    {
      id: addressTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h4',
      name: 'Address Title',
      parentId: addressContentId,
      hierarchyName: getHierarchyName('h4'),
      className: infoTitleClass,
      styles: {},
      children: [],
      text: 'Studio Location',
      attributes: {
        'data-element': 'contact-info-title'
      }
    },

    // Address Text
    {
      id: addressTextId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Address Text',
      parentId: addressContentId,
      hierarchyName: getHierarchyName('p'),
      className: infoTextClass,
      styles: {},
      children: [],
      text: '123 Photography Street, Creative District, New York, NY 10001',
      attributes: {
        'data-element': 'contact-info-text'
      }
    },

    // Phone Item
    {
      id: phoneItemId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Phone Item',
      parentId: infoContainerId,
      hierarchyName: getHierarchyName('div'),
      className: infoItemClass,
      styles: {},
      children: [phoneIconId, phoneContentId],
      text: '',
      attributes: {
        'data-element': 'contact-info-item'
      }
    },

    // Phone Icon
    {
      id: phoneIconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Phone Icon',
      parentId: phoneItemId,
      hierarchyName: getHierarchyName('div'),
      className: infoIconClass,
      styles: {},
      children: [],
      text: '📞',
      attributes: {
        'data-element': 'contact-info-icon'
      }
    },

    // Phone Content
    {
      id: phoneContentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Phone Content',
      parentId: phoneItemId,
      hierarchyName: getHierarchyName('div'),
      className: infoContentClass,
      styles: {},
      children: [phoneTitleId, phoneTextId],
      text: '',
      attributes: {
        'data-element': 'contact-info-content'
      }
    },

    // Phone Title
    {
      id: phoneTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h4',
      name: 'Phone Title',
      parentId: phoneContentId,
      hierarchyName: getHierarchyName('h4'),
      className: infoTitleClass,
      styles: {},
      children: [],
      text: 'Call Us',
      attributes: {
        'data-element': 'contact-info-title'
      }
    },

    // Phone Text
    {
      id: phoneTextId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Phone Text',
      parentId: phoneContentId,
      hierarchyName: getHierarchyName('p'),
      className: infoTextClass,
      styles: {},
      children: [],
      text: '+1 (555) 123-4567',
      attributes: {
        'data-element': 'contact-info-text'
      }
    },

    // Email Item
    {
      id: emailItemId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Email Item',
      parentId: infoContainerId,
      hierarchyName: getHierarchyName('div'),
      className: infoItemClass,
      styles: {},
      children: [emailIconId, emailContentId],
      text: '',
      attributes: {
        'data-element': 'contact-info-item'
      }
    },

    // Email Icon
    {
      id: emailIconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Email Icon',
      parentId: emailItemId,
      hierarchyName: getHierarchyName('div'),
      className: infoIconClass,
      styles: {},
      children: [],
      text: '✉️',
      attributes: {
        'data-element': 'contact-info-icon'
      }
    },

    // Email Content
    {
      id: emailContentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Email Content',
      parentId: emailItemId,
      hierarchyName: getHierarchyName('div'),
      className: infoContentClass,
      styles: {},
      children: [emailTitleId, emailTextId],
      text: '',
      attributes: {
        'data-element': 'contact-info-content'
      }
    },

    // Email Title
    {
      id: emailTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h4',
      name: 'Email Title',
      parentId: emailContentId,
      hierarchyName: getHierarchyName('h4'),
      className: infoTitleClass,
      styles: {},
      children: [],
      text: 'Email Us',
      attributes: {
        'data-element': 'contact-info-title'
      }
    },

    // Email Text
    {
      id: emailTextId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Email Text',
      parentId: emailContentId,
      hierarchyName: getHierarchyName('p'),
      className: infoTextClass,
      styles: {},
      children: [],
      text: 'hello@photographystudio.com',
      attributes: {
        'data-element': 'contact-info-text'
      }
    },

    // Social Container
    {
      id: socialContainerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Social Container',
      parentId: leftColumnId,
      hierarchyName: getHierarchyName('div'),
      className: socialContainerClass,
      styles: {},
      children: [socialTitleId, socialLinksId],
      text: '',
      attributes: {
        'data-element': 'contact-social-container'
      }
    },

    // Social Title
    {
      id: socialTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Social Title',
      parentId: socialContainerId,
      hierarchyName: getHierarchyName('h3'),
      className: socialTitleClass,
      styles: {},
      children: [],
      text: 'Follow Our Work',
      attributes: {
        'data-element': 'contact-social-title'
      }
    },

    // Social Links
    {
      id: socialLinksId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Social Links',
      parentId: socialContainerId,
      hierarchyName: getHierarchyName('div'),
      className: socialLinksClass,
      styles: {},
      children: [instagramLinkId, facebookLinkId, twitterLinkId],
      text: '',
      attributes: {
        'data-element': 'contact-social-links'
      }
    },

    // Instagram Link
    {
      id: instagramLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Instagram Link',
      parentId: socialLinksId,
      hierarchyName: getHierarchyName('a'),
      className: socialLinkClass,
      styles: {},
      children: [],
      text: '📷',
      attributes: {
        'data-element': 'contact-social-link',
        'href': 'https://instagram.com',
        'target': '_blank',
        'rel': 'noopener noreferrer'
      }
    },

    // Facebook Link
    {
      id: facebookLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Facebook Link',
      parentId: socialLinksId,
      hierarchyName: getHierarchyName('a'),
      className: socialLinkClass,
      styles: {},
      children: [],
      text: '📘',
      attributes: {
        'data-element': 'contact-social-link',
        'href': 'https://facebook.com',
        'target': '_blank',
        'rel': 'noopener noreferrer'
      }
    },

    // Twitter Link
    {
      id: twitterLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Twitter Link',
      parentId: socialLinksId,
      hierarchyName: getHierarchyName('a'),
      className: socialLinkClass,
      styles: {},
      children: [],
      text: '🐦',
      attributes: {
        'data-element': 'contact-social-link',
        'href': 'https://twitter.com',
        'target': '_blank',
        'rel': 'noopener noreferrer'
      }
    },

    // Form Container
    {
      id: formContainerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Form Container',
      parentId: rightColumnId,
      hierarchyName: getHierarchyName('div'),
      className: formContainerClass,
      styles: {},
      children: [formTitleId, formId],
      text: '',
      attributes: {
        'data-element': 'contact-form-container'
      }
    },

    // Form Title
    {
      id: formTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Form Title',
      parentId: formContainerId,
      hierarchyName: getHierarchyName('h3'),
      className: formTitleClass,
      styles: {},
      children: [],
      text: 'Send Us a Message',
      attributes: {
        'data-element': 'contact-form-title'
      }
    },

    // Form
    {
      id: formId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'form',
      name: 'Contact Form',
      parentId: formContainerId,
      hierarchyName: getHierarchyName('form'),
      className: formClass,
      styles: {},
      children: [nameEmailRowId, subjectGroupId, messageGroupId, submitButtonId],
      text: '',
      attributes: {
        'data-element': 'contact-form',
        'method': 'post',
        'action': ''
      }
    },

    // Name and Email Row
    {
      id: nameEmailRowId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Name and Email Row',
      parentId: formId,
      hierarchyName: getHierarchyName('div'),
      className: formRowClass,
      styles: {},
      children: [nameGroupId, emailGroupId],
      text: '',
      attributes: {
        'data-element': 'contact-form-row'
      }
    },

    // Name Group
    {
      id: nameGroupId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Name Group',
      parentId: nameEmailRowId,
      hierarchyName: getHierarchyName('div'),
      className: formGroupClass,
      styles: {},
      children: [nameLabelId, nameInputId],
      text: '',
      attributes: {
        'data-element': 'contact-form-group'
      }
    },

    // Name Label
    {
      id: nameLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Name Label',
      parentId: nameGroupId,
      hierarchyName: getHierarchyName('label'),
      className: formLabelClass,
      styles: {},
      children: [],
      text: 'Your Name',
      attributes: {
        'data-element': 'contact-form-label',
        'for': 'contact-name'
      }
    },

    // Name Input
    {
      id: nameInputId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Name Input',
      parentId: nameGroupId,
      hierarchyName: getHierarchyName('input'),
      className: formInputClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'contact-form-input',
        'type': 'text',
        'id': 'contact-name',
        'name': 'name',
        'placeholder': 'Enter your full name',
        'required': 'true'
      }
    },

    // Email Group
    {
      id: emailGroupId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Email Group',
      parentId: nameEmailRowId,
      hierarchyName: getHierarchyName('div'),
      className: formGroupClass,
      styles: {},
      children: [emailLabelId, emailInputId],
      text: '',
      attributes: {
        'data-element': 'contact-form-group'
      }
    },

    // Email Label
    {
      id: emailLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Email Label',
      parentId: emailGroupId,
      hierarchyName: getHierarchyName('label'),
      className: formLabelClass,
      styles: {},
      children: [],
      text: 'Email Address',
      attributes: {
        'data-element': 'contact-form-label',
        'for': 'contact-email'
      }
    },

    // Email Input
    {
      id: emailInputId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Email Input',
      parentId: emailGroupId,
      hierarchyName: getHierarchyName('input'),
      className: formInputClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'contact-form-input',
        'type': 'email',
        'id': 'contact-email',
        'name': 'email',
        'placeholder': 'Enter your email address',
        'required': 'true'
      }
    },

    // Subject Group
    {
      id: subjectGroupId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Subject Group',
      parentId: formId,
      hierarchyName: getHierarchyName('div'),
      className: formGroupClass,
      styles: {},
      children: [subjectLabelId, subjectInputId],
      text: '',
      attributes: {
        'data-element': 'contact-form-group'
      }
    },

    // Subject Label
    {
      id: subjectLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Subject Label',
      parentId: subjectGroupId,
      hierarchyName: getHierarchyName('label'),
      className: formLabelClass,
      styles: {},
      children: [],
      text: 'Subject',
      attributes: {
        'data-element': 'contact-form-label',
        'for': 'contact-subject'
      }
    },

    // Subject Input
    {
      id: subjectInputId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Subject Input',
      parentId: subjectGroupId,
      hierarchyName: getHierarchyName('input'),
      className: formInputClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'contact-form-input',
        'type': 'text',
        'id': 'contact-subject',
        'name': 'subject',
        'placeholder': 'What can we help you with?',
        'required': 'true'
      }
    },

    // Message Group
    {
      id: messageGroupId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Message Group',
      parentId: formId,
      hierarchyName: getHierarchyName('div'),
      className: formGroupClass,
      styles: {},
      children: [messageLabelId, messageTextareaId],
      text: '',
      attributes: {
        'data-element': 'contact-form-group'
      }
    },

    // Message Label
    {
      id: messageLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Message Label',
      parentId: messageGroupId,
      hierarchyName: getHierarchyName('label'),
      className: formLabelClass,
      styles: {},
      children: [],
      text: 'Your Message',
      attributes: {
        'data-element': 'contact-form-label',
        'for': 'contact-message'
      }
    },

    // Message Textarea
    {
      id: messageTextareaId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'textarea',
      name: 'Message Textarea',
      parentId: messageGroupId,
      hierarchyName: getHierarchyName('textarea'),
      className: formTextareaClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'contact-form-textarea',
        'id': 'contact-message',
        'name': 'message',
        'placeholder': 'Tell us about your photography needs...',
        'required': 'true',
        'rows': '5'
      }
    },

    // Submit Button
    {
      id: submitButtonId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'Submit Button',
      parentId: formId,
      hierarchyName: getHierarchyName('button'),
      className: formButtonClass,
      styles: {},
      children: [],
      text: 'Send Message',
      attributes: {
        'data-element': 'contact-form-button',
        'type': 'submit'
      }
    }
  ];

  return {
    contactSection: elements[0],
    elements: elements
  };
}; 